/*
 * Copyright (c) 2009 Macrofocus GmbH. All Rights Reserved.
 */
package com.macrofocus.high_d.axis

import com.macrofocus.common.filter.MutableFilter
import com.macrofocus.common.format.CPFormat
import com.macrofocus.common.interval.MutableBoundedInterval
import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.scale.Transform
import com.macrofocus.common.selection.Selection
import com.macrofocus.common.selection.SingleSelection
import org.molap.dataframe.ColumnStatistics
import org.molap.subset.OrdinalDimension
import kotlin.reflect.KClass

interface AxisModel<Row, C> : Iterable<Row> {
    val buttonModel: MutableProperty<Boolean>
    val isNumerical: Boolean
    val name: String
    fun getValue(row: Row): Number?
    fun getValue(layer: Int, row: Row): Number?
    fun getScaledValue(layer: Int, row: Row): Number? {
        return getValue(layer, row)
    }
    fun getNormalizedValue(value: Double): Double? {
        val minimum: Double = this.minimum
        val maximum: Double = this.maximum
        val scale: MutableProperty<Transform>? = this.scale
        if (scale != null) {
            val transform = scale.value
            val v: Double = transform.transform.invoke(value)
            val min: Double = transform.transform.invoke(minimum)
            val max: Double = transform.transform.invoke(maximum)
            return (v - min) / (max - min)
        } else {
            return (value - minimum) / (maximum - minimum)
        }
    }
    var minimum: Double
    val mean: Double
    val stdDev: Double
    var maximum: Double
    val isDegenerate: Boolean
    val rowCount: Int
    fun getRow(row: Int): Row
    val probing: SingleSelection<Row>?
    val selection: Selection<Row>
    val filter: MutableFilter<Row>?
    fun addAxisListener(listener: AxisListener)
    fun removeAxisListener(listener: AxisListener)
    fun setMinMax(min: Double, max: Double)
    val columnStatistics: ColumnStatistics
    val interval: MutableBoundedInterval
    val scaledInterval: MutableBoundedInterval
        get() = interval
    val type: KClass<Any>
    val column: C
    val formattedMinimum: String
    val formattedMaximum: String
    fun getFormattedValue(row: Row): String
    fun getFormattedValue(layer: Int, row: Row): String
    fun getFormattedFloorValue(value: Double): String
    fun getFormattedCeilValue(value: Double): String
    val dimension: OrdinalDimension<*,*,out Any?>?
    fun getOriginalFloorValue(value: Double): Any?
    fun getOriginalCeilValue(value: Double): Any?
    var format: CPFormat<Any?>?
    val binCount: MutableProperty<Int>?
    val binType: MutableProperty<BinType>?
    val scale: MutableProperty<Transform>?

    companion object {
        const val DATA = 0
        const val ANNOTATION = 1
    }
}